import type { ConnectionTestResult } from '@flux/shared';
import { useMutation } from '@tanstack/react-query';
import { useState, type JSX, type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { cn } from '@/lib/cn.js';
import { toast } from '@/lib/toast.js';
import { ConnectionTestMessage } from './settings-parts.js';

/**
 * Pièces de l'onglet Paramètres → Recharge (`06` §8.6), partagées par ses
 * cartes.
 */

/** `aria-describedby` d'un contrôle dont l'aide et l'erreur sont rendues par `FieldMessages`. */
export function describedByIds(id: string, hint: ReactNode, error: string | undefined): string | undefined {
  const ids = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter((value) => value !== null);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

/**
 * Aide puis erreur sous un champ, comme `Field`, mais avec des `id` pour que
 * le contrôle les annonce (`aria-describedby`).
 */
export function FieldMessages({
  id,
  hint,
  error,
}: {
  id: string;
  hint?: ReactNode;
  error?: string | undefined;
}): JSX.Element | null {
  if (!hint && !error) {
    return null;
  }
  return (
    <>
      {hint ? (
        <p id={`${id}-hint`} className="text-xs leading-5 text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={`${id}-error`} className="text-xs text-status-red-fg">
          {error}
        </p>
      ) : null}
    </>
  );
}

export interface SecretFieldProps {
  id: string;
  label: string;
  configured: boolean;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  error?: string | undefined;
  inputMode?: 'text' | 'url';
}

/**
 * Champ secret : jamais pré-rempli ni réaffiché (`06` §7). Un badge dit s'il
 * est configuré ; le champ reste vide et n'est envoyé que s'il a été saisi.
 */
export function SecretField({
  id,
  label,
  configured,
  value,
  onChange,
  hint,
  error,
  inputMode = 'text',
}: SecretFieldProps): JSX.Element {
  return (
    <Field
      label={
        <span className="inline-flex flex-wrap items-center gap-2">
          {label}
          <Badge tone={configured ? 'green' : 'gray'}>{configured ? 'Configuré' : 'Non configuré'}</Badge>
        </span>
      }
      htmlFor={id}
    >
      <Input
        id={id}
        name={id}
        type="password"
        autoComplete="new-password"
        spellCheck={false}
        inputMode={inputMode}
        placeholder={configured ? '••••••••' : undefined}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedByIds(id, hint, error)}
        className="max-w-md"
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <FieldMessages id={id} hint={hint} error={error} />
    </Field>
  );
}

export interface SegmentedOption<TValue extends string> {
  value: TValue;
  label: string;
}

export interface SegmentedRadioGroupProps<TValue extends string> {
  /** `name` commun des boutons radio (unique dans la page). */
  name: string;
  /** Nom accessible du groupe. */
  legend: string;
  /** Légende visible au-dessus du groupe ; sinon seulement lue par les lecteurs d'écran. */
  showLegend?: boolean;
  options: readonly SegmentedOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  disabled?: boolean;
  /** `id` de l'aide affichée sous le groupe. */
  describedBy?: string | undefined;
}

/**
 * Contrôle segmenté construit sur de vrais boutons radio : flèches pour
 * changer de valeur, `Tab` pour sortir du groupe. Même rendu que les boutons
 * bascule de `schedule-editor.tsx`.
 */
export function SegmentedRadioGroup<TValue extends string>({
  name,
  legend,
  showLegend = false,
  options,
  value,
  onChange,
  disabled = false,
  describedBy,
}: SegmentedRadioGroupProps<TValue>): JSX.Element {
  return (
    <fieldset className="flex min-w-0 flex-col gap-1.5" disabled={disabled} aria-describedby={describedBy}>
      <legend className={showLegend ? 'mb-1.5 text-sm font-medium leading-5' : 'sr-only'}>{legend}</legend>
      <div className="flex flex-wrap gap-1">
        {options.map((option) => (
          <label key={option.value} className={cn('relative', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              className="peer sr-only"
              onChange={() => {
                onChange(option.value);
              }}
            />
            <span
              className={cn(
                'inline-flex h-8 min-w-11 items-center justify-center rounded-md border px-3 text-sm font-medium transition-colors',
                'border-input bg-card text-foreground hover:bg-accent',
                'peer-checked:border-primary peer-checked:bg-primary peer-checked:text-primary-foreground',
                'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background',
                'peer-disabled:opacity-60 peer-disabled:hover:bg-card',
              )}
            >
              {option.label}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Bouton « Tester » secondaire : résultat en toast et rappelé à côté du
 * bouton, pour qu'un échec ne repose pas sur le seul toast.
 */
export function ConnectionTestButton({
  label,
  run,
}: {
  label: string;
  run: () => Promise<ConnectionTestResult>;
}): JSX.Element {
  const [result, setResult] = useState<ConnectionTestResult | null>(null);
  const mutation = useMutation({
    mutationFn: run,
    onSuccess: (data) => {
      setResult(data);
      if (data.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    },
    onError: (error: unknown) => {
      setResult(null);
      toast.fromError(error);
    },
  });

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <Button
        type="button"
        variant="outline"
        isLoading={mutation.isPending}
        onClick={() => {
          mutation.mutate();
        }}
      >
        {label}
      </Button>
      {result ? (
        <span aria-live="polite" className="min-w-0">
          <ConnectionTestMessage
            ok={result.ok}
            message={result.message}
            detail={result.detail}
            durationMs={result.durationMs}
          />
        </span>
      ) : null}
    </div>
  );
}
